
import { Component, inject, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { GrnService } from '../../grn/grn.service';
import { NgSelectComponent } from '@ng-select/ng-select';
import { PickListService } from '../pick-list.service';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute } from '@angular/router'; // Import ActivatedRoute
import { Router } from '@angular/router';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-pick-list',
  imports: [FormsModule, NgSelectComponent, RouterModule],
  templateUrl: './pick-list.component.html',
  styleUrl: './pick-list.component.scss',
})
export class PickListComponent {
  constructor(private router: Router) {}
  sourceLocationId: string = '';
  userData = JSON.parse(localStorage.getItem('user') || '{}');
  private grnService = inject(GrnService);
  private pickListService = inject(PickListService);
  private toastrService = inject(ToastrService);
  private route = inject(ActivatedRoute); // Inject ActivatedRoute
  private picklistId: string = '';
  grns: any[] = [];
  selectedGrnId: string = '';
  picklistData: any[] = [];
  storeKeys: string[] = [];
  viewOnly = false;
  isEditMode: boolean = false;
  pickListNumber: string = '';

  ngOnInit(): void {
    this.sourceLocationId = this.userData.primaryLocationId;

    // ✅ Subscribe to both paramMap and queryParams to get all route data
    this.route.paramMap.subscribe((params) => {
      const picklistId = params.get('id');

      this.route.queryParams.subscribe((queryParams) => {
        this.viewOnly = queryParams['view'] === 'true';

        if (picklistId) {
          // We have a picklist ID, now check if it's view-only
          this.picklistId = picklistId;

          if (this.viewOnly) {
            // It's a view-only picklist (e.g., /picklist/ID?view=true)
            this.isEditMode = false;
            console.log(`View picklist: ${picklistId}`);
            this.loadPickListForEdit(picklistId); // You can reuse this method to load data for viewing
          } else {
            // It's a picklist that can be edited (e.g., /picklist/ID)
            this.isEditMode = true;
            console.log(`Edit picklist: ${picklistId}`);
            this.loadPickListForEdit(picklistId);
          }
        } else {
          // No ID in the URL, so it's a new picklist
          this.isEditMode = false;
          console.log(`Create picklist`);
          this.loadPostedGrns();
        }
      });
    });
  }

  // Union of store codes across ALL rows — a store may appear only on some
  // SKUs, so deriving columns from rows[0] alone can drop stores entirely.
  private computeStoreKeys(rows: any[]): string[] {
    const keys = new Set<string>();
    for (const row of rows || []) {
      for (const k of Object.keys(row.indents || {})) {
        if (k !== 'TOTAL') keys.add(k);
      }
      for (const k of Object.keys(row.finalPicklist || {})) {
        if (k !== 'TOTAL') keys.add(k);
      }
    }
    return Array.from(keys);
  }

  private loadPickListForEdit(pickListId: string): void {
    this.pickListService.getPickListDetails(pickListId).subscribe({
      next: (data: any) => {
        console.log('Picklist details for edit:', data);
        this.pickListNumber = data.picklistNumber;
        if (data?.rows?.length) {
          // Set the selectedGrnId from the API response
          this.selectedGrnId = data.grnId;

          this.picklistData = data.rows.map((row: any) => {
            const allocStores = Object.keys(row.finalPicklist);
            const allocTotal = allocStores.reduce(
              (sum, s) => sum + (row.finalPicklist[s] || 0),
              0
            );
            row.finalPicklist.TOTAL = allocTotal;

            if (!row.indents.TOTAL) {
              const indentStores = Object.keys(row.indents);
              const indentTotal = indentStores.reduce(
                (sum, s) => (s !== 'TOTAL' ? sum + (row.indents[s] || 0) : sum),
                0
              );
              row.indents.TOTAL = indentTotal;
            }
            return row;
          });

          this.storeKeys = this.computeStoreKeys(data.rows);
        }
      },
      error: (error: any) => {
        console.error('Error fetching picklist details for edit:', error);
        this.toastrService.error('Failed to load picklist for editing.');
      },
    });
  }

  private loadPostedGrns(): void {
    this.grnService.getPostedGrns().subscribe({
      next: (data: any) => {
        this.grns = data;
        console.log('Posted GRNs:', data);
      },
      error: (error: any) => {
        console.error('Error fetching posted GRNs:', error);
      },
    });
  }

  private loadPostedPickLists(): void {
    this.grnService.getPostedGrns().subscribe({
      next: (data: any) => {
        this.grns = data;
        console.log('Posted GRNs:', data);

        // Check for a GRN ID in the URL and process it
        this.route.paramMap.subscribe((params) => {
          const pickListId = params.get('id'); // 'grnId' should match the parameter name in your route configuration
          const grnId = params.get('grnId');
          if (pickListId) {
            this.pickListService.getPickListDetails(pickListId).subscribe({
              next: (data: any) => {
                if (data?.rows?.length) {
                  this.picklistData = data.rows.map((row: any) => {
                    // Ensure TOTAL exists in finalPicklist
                    const allocStores = Object.keys(row.finalPicklist);
                    const allocTotal = allocStores.reduce(
                      (sum, s) => sum + (row.finalPicklist[s] || 0),
                      0
                    );
                    row.finalPicklist.TOTAL = allocTotal;

                    // Ensure TOTAL exists in indents
                    if (!row.indents.TOTAL) {
                      const indentStores = Object.keys(row.indents);
                      const indentTotal = indentStores.reduce(
                        (sum, s) =>
                          s !== 'TOTAL' ? sum + (row.indents[s] || 0) : sum,
                        0
                      );
                      row.indents.TOTAL = indentTotal;
                    }

                    return row;
                  });

                  // collect store names dynamically
                  this.storeKeys = this.computeStoreKeys(data.rows);
                }
              },
            });
          }
        });
      },
      error: (error: any) => {
        console.error('Error fetching posted GRNs:', error);
      },
    });
  }

  onGrnChange(grn: any) {
    if (!grn) {
      console.warn('onGrnChange called without a GRN object');
      return;
    }
    this.selectedGrnId = grn?.grnId || '';
    const body = { sourceLocationId: this.sourceLocationId, grnId: grn.grnId };
    this.pickListService.generatePickList(body).subscribe({
      next: (data: any) => {
        if (data?.rows?.length) {
          this.picklistData = data.rows.map((row: any) => {
            // Ensure TOTAL exists in finalPicklist
            const allocStores = Object.keys(row.finalPicklist);
            const allocTotal = allocStores.reduce(
              (sum, s) => sum + (row.finalPicklist[s] || 0),
              0
            );
            row.finalPicklist.TOTAL = allocTotal;

            // Ensure TOTAL exists in indents
            if (!row.indents.TOTAL) {
              const indentStores = Object.keys(row.indents);
              const indentTotal = indentStores.reduce(
                (sum, s) => (s !== 'TOTAL' ? sum + (row.indents[s] || 0) : sum),
                0
              );
              row.indents.TOTAL = indentTotal;
            }

            return row;
          });

          // collect store names dynamically
          this.storeKeys = this.computeStoreKeys(data.rows);
        }
      },
    });
  }

  savePickList() {
    if (!this.selectedGrnId || !this.picklistData.length) {
      this.toastrService.warning('Select a GRN and generate a picklist first.');
      return;
    }

    if (this.isEditMode && this.picklistId) {
      // ----------------------------------------------------
      // ✅ EDIT MODE: Call the PATCH endpoint to update the status
      // ----------------------------------------------------
      const updatePayload = {
        status: 'POSTED',
      };

      this.pickListService
        .updatePickListStatus(this.picklistId, updatePayload)
        .subscribe({
          next: () => {
            this.toastrService.success(
              'Pick List updated and posted successfully!'
            );
            this.router.navigate(['/picklist']);
          },
          error: (error: any) => {
            console.error('Error updating Pick List:', error);
            this.toastrService.error(
              'Error updating Pick List status.',
              error?.error?.message
            );
          },
        });
    } else {
      // ----------------------------------------------------
      // ✅ CREATE MODE: Call the POST endpoint to create a new picklist
      // ----------------------------------------------------
      const picklistNumber = this.pickListNumber;
      console.log('picklistData:', this.picklistData);

      const payload = {
        grnId: this.selectedGrnId,
        sourceLocationId: this.sourceLocationId,
        strategy: 'PROPORTIONAL',
        capToIndent: true,
        respectReorderMultiple: true,
        storePriority: ['MK01', 'JH01'],
        rows: this.picklistData.map((row) => ({
          variantId: row.variantId,
          sku: row.sku,
          productName: row.productName,
          grnQty: row.grnQty,
          indents: row.indents,
          contributionPercent: row.contributionPercent,
          picklistRaw: row.picklistRaw,
          picklistRounded: row.picklistRounded,
          finalPicklist: row.finalPicklist,
          indentIds: row.indentIds,
          ok: row.ok,
        })),
        summary: {
          variants: this.picklistData.length,
          grnTotal: this.picklistData.reduce((sum, r) => sum + r.grnQty, 0),
          indentTotal: this.picklistData.reduce(
            (sum, r) => sum + r.indents.TOTAL,
            0
          ),
          finalPicklistTotal: this.picklistData.reduce(
            (sum, r) => sum + r.finalPicklist.TOTAL,
            0
          ),
        },
        picklistNumber: picklistNumber,
        status: 'POSTED',
      };

      this.pickListService.savePickList(payload).subscribe({
        next: () => {
          this.toastrService.success(
            'Pick List saved and posted successfully!'
          );
          this.router.navigate(['/picklist']);
        },
        error: (error: any) => {
          console.error('Error saving Pick List:', error);
          this.toastrService.error('Error saving Pick List');
        },
      });
    }
  }

  cancel() {
    this.loadPostedGrns();
    this.selectedGrnId = '';
    this.picklistData = [];
    this.storeKeys = [];
  }

  printTable() {
    const printContent = document.getElementById('printSection');
    const WindowPrt = window.open(
      '',
      '',
      'left=0,top=0,width=900,height=900,toolbar=0,scrollbars=0,status=0'
    );
    if (WindowPrt && printContent) {
      WindowPrt.document.write(`
        <html>
          <head>
            <title>GRN Allocation Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .print-header { text-align: center; margin-bottom: 20px; }
              .print-date { text-align: right; margin-bottom: 10px; font-style: italic; }
              table { width: 100%; border-collapse: collapse; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: center; }
              th { background-color: #f2f2f2; }
            </style>
          </head>
          <body>
            <div class="print-header">
              <h2>GRN Allocation Report</h2>
              <div class="print-date">Generated on: ${new Date().toLocaleDateString()}</div>
            </div>
            ${printContent.innerHTML}
          </body>
        </html>
      `);
      WindowPrt.document.close();
      WindowPrt.focus();
      WindowPrt.print();
      WindowPrt.close();
    }
  }
  saveDraft() {
    if (this.selectedGrnId && this.picklistData.length) {
      this.pickListService.getNextPickListNumber().subscribe((res: any) => {
        const nextNumber = String(res.grnNumber || res);

        const payload = {
          grnId: this.selectedGrnId,
          sourceLocationId: this.sourceLocationId,
          strategy: 'PROPORTIONAL',
          capToIndent: true,
          respectReorderMultiple: true,
          storePriority: ['MK01', 'JH01'],
          rows: this.picklistData.map((row) => ({
            variantId: row.variantId,
            sku: row.sku,
            productName: row.productName,
            grnQty: row.grnQty,
            indents: row.indents,
            contributionPercent: row.contributionPercent,
            picklistRaw: row.picklistRaw,
            picklistRounded: row.picklistRounded,
            finalPicklist: row.finalPicklist,
            indentIds: row.indentIds,
            ok: row.ok,
          })),
          summary: {
            variants: this.picklistData.length,
            grnTotal: this.picklistData.reduce((sum, r) => sum + r.grnQty, 0),
            indentTotal: this.picklistData.reduce(
              (sum, r) => sum + r.indents.TOTAL,
              0
            ),
            finalPicklistTotal: this.picklistData.reduce(
              (sum, r) => sum + r.finalPicklist.TOTAL,
              0
            ),
          },
          picklistNumber: nextNumber, // ✅ ensured string
          status: 'DRAFT',
        };

        this.pickListService.savePickList(payload).subscribe({
          next: () => {
            this.toastrService.success(
              'Pick List number ' + nextNumber + ' saved as draft successfully'
            );
            this.router.navigate(['/picklist']);

            this.cancel();
          },
          error: (err) => {
            console.error('Error saving draft:', err);
            this.toastrService.error('Failed to save draft');
          },
        });
      });
    } else {
      this.toastrService.warning('Select a GRN and generate picklist first.');
    }
  }
}
